/**
 * ResolutionMark — the settled-state glyph for a proposal row in chat.
 *
 * A pending proposal is a decision, so it wears a status pill. A resolved
 * one is history: an event that already happened. History gets a mark, not
 * a pill — a single circled glyph in the outcome's colour with the outcome
 * word beside it, the way a finished item reads in an activity feed.
 *
 * `executing` is still live, so it keeps the pill and never reaches here
 * (`isSettledResolution` in resolutionStyles decides which rows get a mark).
 */

import {
  AlertCircle,
  CheckCircle,
  MinusCircle,
  XCircle,
} from '@untitledui/icons';
import type { FC, SVGProps } from 'react';

import { getResolutionStyles } from '@/lib/proposals/utils/resolutionStyles';
import { cx } from '@/utils/cx';

interface MarkStyle {
  Icon: FC<SVGProps<SVGSVGElement>>;
  iconClass: string;
}

const MARKS: Record<string, MarkStyle> = {
  approved: {
    Icon: CheckCircle,
    iconClass: 'text-utility-success-600 dark:text-utility-success-400',
  },
  rejected: { Icon: XCircle, iconClass: 'text-fg-quaternary' },
  withdrawn: { Icon: MinusCircle, iconClass: 'text-fg-quaternary' },
  failed: {
    Icon: AlertCircle,
    iconClass: 'text-utility-error-600 dark:text-utility-error-400',
  },
};

export interface ResolutionMarkProps {
  resolution: string;
  /**
   * When true only the glyph renders (still labelled for screen readers).
   * Used where the row's own text already states the outcome, e.g.
   * "Moved Acme to Qualification".
   */
  iconOnly?: boolean;
}

export function ResolutionMark({
  resolution,
  iconOnly = false,
}: ResolutionMarkProps) {
  const mark = MARKS[resolution];
  if (!mark) {
    return null;
  }
  const { label } = getResolutionStyles(resolution);
  const { Icon, iconClass } = mark;
  return (
    <span
      className='flex items-center gap-1.5 shrink-0'
      data-testid='resolution-mark'
      role={iconOnly ? 'img' : undefined}
      aria-label={iconOnly ? label : undefined}
    >
      <Icon className={cx('w-4 h-4', iconClass)} aria-hidden='true' />
      {!iconOnly && (
        <span className='text-xs font-medium text-fg-tertiary'>{label}</span>
      )}
    </span>
  );
}
